import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime } from 'rxjs/operators';

import { ConfirmationResult, ConfirmationService, ModalService, ErrorTranslationService } from '@bayclubs/utils';
import { BayclubsAlertService } from '@bayclubs/alert';

import { campsenum } from '../../../../models/enums.generated';
import { camps } from '../../../../models/models.generated';
import { CampCareApiService } from '../../../../services/api/camp-care-api.service';
import { CampsDetailsFormStateService } from '../camp-details-form-state-service';
import { CampCareDaySingleEditComponent } from './camp-care-day-single-edit/camp-care-day-single-edit.component';
import { CampCareDaysGeneratorComponent } from './camp-care-days-generator/camp-care-days-generator.component';

@Component({
    selector: 'app-camp-care',
    templateUrl: './camp-care.component.html',
    styleUrls: ['./camp-care.component.scss'],
    standalone: false
})
export class CampCareComponent implements OnInit {
    @Input() public campForm: UntypedFormGroup;
    public campCareModel: camps.CampCareDetails;

    constructor(
        private campCareApi: CampCareApiService,
        private modal: ModalService,
        private alert: BayclubsAlertService,
        private confirmation: ConfirmationService,
        private translate: TranslateService,
        public state: CampsDetailsFormStateService,
        private errorTranslate: ErrorTranslationService
    ) {}

    ngOnInit(): void {
        this.load();

        this.campForm.valueChanges.pipe(debounceTime(500)).subscribe(() => {
            this.load();
        });
    }

    public openGenerator(): void {
        this.modal
            .create<CampCareDaysGeneratorComponent>(CampCareDaysGeneratorComponent, { campForm: this.campForm })
            .subscribe(component => {
                console.log(component);
                console.log(component.result);
                if (component.result && component.result.succeed.length > 0) {
                    this.load();
                }
            });
    }

    public addNew(): void {
        this.modal
            .create<CampCareDaySingleEditComponent>(CampCareDaySingleEditComponent, { campForm: this.campForm })
            .subscribe(component => {
                if (component.result) {
                    this.load();
                }
            });
    }

    private load() {
        this.campCareApi.get(this.getCampId()).subscribe(result => {
            this.campCareModel = result.value;
            this.isCampCareActive = this.isActive();
        });
    }

    isDisabled(): boolean {
        return this.campCareModel && this.campCareModel.status == campsenum.CampCareStatus.Disabled;
    }

    isClosed(): boolean {
        return this.campCareModel && this.campCareModel.status == campsenum.CampCareStatus.Closed;
    }

    isActive(): boolean {
        return this.campCareModel && this.campCareModel.status == campsenum.CampCareStatus.Active;
    }

    public isCampCareActive: boolean;

    public onVisibilityChange() {
        if (this.isDisabled() || this.isClosed()) {
            this.activate();
        }

        if (this.isActive()) {
            this.close();
        }
    }

    public activate() {
        console.log('Activating...');

        this.confirmation.show(null, this.translate.instant('CampCare.ActivateConfirmation')).subscribe(result => {
            if (result) {
                this.campCareApi
                    .activate(<camps.ActivateCampCareRequest>{
                        campId: this.getCampId()
                    })
                    .subscribe(result => {
                        if (result.isSuccess) {
                            this.campCareModel.status = campsenum.CampCareStatus.Active;
                            this.alert.showOperationSuccess();
                        } else {
                            this.isCampCareActive = false;
                            this.errorTranslate.translateValidationNotifications(result.validationNotification);
                            this.alert.showError(result.validationNotification.errors[0].message);
                        }
                    });
            } else {
                this.isCampCareActive = false;
            }
        });
    }

    public close() {
        console.log('Deactivating...');

        var request = <camps.CloseCampCareRequest>{
            campId: this.getCampId()
        };

        this.confirmation.show(null, this.translate.instant('CampCare.CloseConfirmation')).subscribe(result => {
            if (result) {
                this.closeCampCare(request);
            } else {
                this.isCampCareActive = true;
            }
        });
    }

    private closeCampCare(request) {
        this.campCareApi.close(request).subscribe(result => {
            if (result.isSuccess) {
                this.campCareModel.status = campsenum.CampCareStatus.Closed;
                this.alert.showOperationSuccess();
            } else {
                if (result.validationNotification.errors[0].code == campsenum.ErrorCode.CampCareHasFutureBookings) {
                    this.handleExistingFutureBookings(request);
                } else {
                    this.errorTranslate.translateValidationNotifications(result.validationNotification);
                    this.alert.showError(result.validationNotification.errors[0].message);

                    this.isCampCareActive = true;
                }
            }
        });
    }

    private handleExistingFutureBookings(request: camps.CloseCampCareRequest) {
        this.confirmation
            .showTriState(
                null,
                this.translate.instant('CampCare.CloseConfirmationWithBookings'),
                this.translate.instant('CampCare.CloseConfirmationCancelBookings'),
                this.translate.instant('CampCare.CloseConfirmationKeepExisting'),
                this.translate.instant('CampCare.CloseConfirmationSaveBookings')
            )
            .subscribe(result => {
                if (result == ConfirmationResult.Cancel) {
                    this.isCampCareActive = true;
                    return;
                }

                if (result == ConfirmationResult.Action1) {
                    request.cancelFutureBookings = true;
                }

                if (result == ConfirmationResult.Action2) {
                    request.cancelFutureBookings = false;
                }

                this.closeCampCare(request);
            });
    }

    private getCampId() {
        return this.campForm.value.basicInfo.id;
    }
}
